/**
 * AziendePage — Macro-hub polizze per imprese e partite IVA.
 *
 * Elenco polizze con CAT NAT in prima posizione (obbligo ex L. 213/2023).
 * Le polizze diverse da CAT NAT reindirizzano per ora a un form generico
 * "contattaci per informazioni" con tipologia pre-compilata (placeholder).
 */
import { useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  AlertTriangle, ArrowRight, Briefcase, Building2, Shield, Users,
  Car, FileSignature, Phone, Factory, ClipboardCheck,
  HeartPulse, CheckCircle, PiggyBank, Palette, ShieldCheck, Scale,
} from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";

// ============================================================================
// ELENCO POLIZZE AZIENDE
// ============================================================================

// Mapping ID card → backend tipo_polizza slug per query param ?polizza=
const AZIENDA_ID_TO_BACKEND_SLUG = {
  rc_professionale: "rc_professionale",
  rc_impresa: "rc_professionale",
  all_risks: "property_business",
  cyber: "cyber_risk",
  d_o: "d_and_o",
  infortuni_dip: "infortuni_dipendenti",
  flotta: "flotta_veicoli",
  perdite_pec: "property_business",
  tutela_legale: "tutela_legale",
};

const POLIZZE_AZIENDE = [
  {
    id: "catnat",
    obbligatoria: true,
    icon: AlertTriangle,
    nome: "CAT NAT — Catastrofi Naturali",
    short: "Obbligo di legge L. 213/2023 art. 1 c. 101-111. Copertura da sisma, alluvione e frana per tutte le imprese iscritte al Registro.",
    href: "/aziende/catnat",
    ctaLabel: "Quota subito",
    ctaHref: "/aziende/catnat/quotatore",
    tagText: "OBBLIGATORIA 2026",
    tagColor: "bg-amber-500 text-white",
  },
  {
    id: "commercio",
    icon: Building2,
    nome: "Polizza Commercio — Negozi e Attività",
    short: "Assicurazione dedicata a ristoranti, negozi, esercizi commerciali e PMI. Include incendio, furto, RC verso terzi e interruzione attività.",
    ctaLabel: "Compila il questionario Commercio",
    href: "/polizza-commercio",
    ctaHref: "/polizza-commercio",
  },
  {
    id: "rc_professionale",
    icon: FileSignature,
    nome: "RC Professionale",
    short: "Responsabilità civile per professionisti iscritti ad albi (avvocati, commercialisti, architetti, medici, ingegneri).",
    ctaLabel: "Richiedi preventivo",
    comingSoon: true,
  },
  {
    id: "rc_impresa",
    icon: Building2,
    nome: "RC Operativa d'Impresa",
    short: "RC verso terzi e prestatori d'opera per danni materiali/personali causati nell'esercizio dell'attività.",
    ctaLabel: "Richiedi preventivo",
    comingSoon: true,
  },
  {
    id: "all_risks",
    icon: Shield,
    nome: "All Risks Property",
    short: "Polizza completa su fabbricati, macchinari, merci e contenuto contro tutti gli eventi salvo espressamente esclusi.",
    ctaLabel: "Richiedi preventivo",
    comingSoon: true,
  },
  {
    id: "cyber",
    icon: ShieldCheck,
    nome: "Cyber Risk",
    short: "Copertura contro attacchi informatici, ransomware, data breach e responsabilità digitale. Ideale per aziende con clienti e dipendenti online.",
    ctaLabel: "Compila il questionario Cyber",
    href: "/cyber",
    ctaHref: "/cyber",
  },
  {
    id: "d_o",
    icon: Scale,
    nome: "D&O — Directors & Officers",
    short: "Responsabilità civile per amministratori, dirigenti e sindaci. Protegge il patrimonio personale in caso di errori di gestione, decisioni contestate o azioni sociali.",
    ctaLabel: "Compila il questionario D&O",
    href: "/do",
    ctaHref: "/do",
  },
  {
    id: "fine_arts_azienda",
    icon: Palette,
    nome: "Fine Arts — Opere d'Arte Aziendali",
    short: "Copertura per opere d'arte esposte, in transito o custodite dalla tua azienda. Ideale per gallerie, musei aziendali, uffici di rappresentanza e collezioni corporate.",
    ctaLabel: "Compila il questionario Fine Arts",
    href: "/questionario/fine-arts",
    ctaHref: "/questionario/fine-arts",
  },
  {
    id: "infortuni_dip",
    icon: HeartPulse,
    nome: "Infortuni Dipendenti",
    short: "Indennità giornaliera, invalidità permanente e caso morte per i dipendenti in servizio e in itinere.",
    ctaLabel: "Richiedi preventivo",
  },
  {
    id: "flotta",
    icon: Car,
    nome: "Flotta Aziendale",
    short: "Gestione unificata RC Auto, ARD e garanzie accessorie per veicoli aziendali e di rappresentanza.",
    ctaLabel: "Richiedi preventivo",
  },
  {
    id: "perdite_pec",
    icon: ClipboardCheck,
    nome: "Perdite Pecuniarie & Business Interruption",
    short: "Indennizzo per fermo attività, mancato utile e costi fissi a seguito di sinistri materiali.",
    ctaLabel: "Richiedi preventivo",
  },
  {
    id: "fondi_pensione",
    icon: PiggyBank,
    nome: "Fondi Pensione — PIP Reale Mutua",
    short: "Welfare aziendale: Piano Individuale Pensionistico per titolari, soci e dipendenti. Vantaggi fiscali fino a 5.164,57 € deducibili per soggetto.",
    href: "/fondi-pensione",
    ctaLabel: "Scopri di più",
    ctaHref: "/fondi-pensione",
  },
];

const VALUE_PROP = [
  { icon: Briefcase, title: "30+ anni di esperienza", desc: "Specializzati nelle soluzioni per imprese e partite IVA" },
  { icon: CheckCircle, title: "Multi-compagnia", desc: "Confronto tra Reale Mutua, UnipolSai, Generali e altri partner" },
  { icon: Phone, title: "Consulente dedicato", desc: "Un referente unico per tutte le polizze aziendali" },
  { icon: Factory, title: "Sopralluogo tecnico", desc: "Valutazione rischi in loco per imprese produttive" },
];

const FAQ_AZIENDE = [
  {
    q: "Quali imprese devono stipulare la polizza CAT NAT?",
    a: "Tutte le imprese iscritte al Registro delle Imprese ai sensi dell'art. 2188 c.c. con sede o attività in Italia. Sono escluse solo le imprese agricole (coperte dal Fondo AgriCat ex L. 197/2022).",
  },
  {
    q: "Cosa succede se non stipulo la CAT NAT?",
    a: "Non ci sono sanzioni amministrative dirette, ma ex art. 1 c. 104 L. 213/2023 la mancata copertura esclude l'impresa da contributi, sovvenzioni e agevolazioni pubbliche post-calamità.",
  },
  {
    q: "Posso unire più polizze in un'unica soluzione?",
    a: "Sì, strutturiamo pacchetti combinati All Risks + CAT NAT + RC + Cyber con gestione unificata e risparmio rispetto alle polizze singole.",
  },
  {
    q: "Ho più sedi operative: come funziona?",
    a: "Le polizze possono coprire fino a 100 ubicazioni sotto un unico contratto, con valori differenziati per ciascuna sede.",
  },
  {
    q: "Serve un sopralluogo tecnico?",
    a: "Per somme assicurate elevate (solitamente oltre €1 mln) o attività a rischio speciale, la compagnia può richiedere sopralluogo di un perito. Organizziamo noi l'appuntamento.",
  },
  {
    q: "Posso pagare a rate?",
    a: "Sì, la maggior parte dei prodotti aziendali prevede rateazione annuale, semestrale o trimestrale. Alcune compagnie ammettono anche il frazionamento mensile.",
  },
];

// ============================================================================
export default function AziendePage() {
  // FIX feb-2026 — preservazione partner tracking (?partner=X&sede=Y&azienda=Z)
  // Se l'utente arriva da una convenzione, propago i query param sui link ai wizard
  // dedicati (Cyber, D&O, Fine Arts) per non perdere l'attribuzione lead → partner.
  const [searchParams] = useSearchParams();
  const _TRACKING_KEYS = ["partner", "utm_source", "sede", "azienda"];
  const trackingQs = (() => {
    const p = new URLSearchParams();
    for (const k of _TRACKING_KEYS) {
      const v = searchParams.get(k);
      if (v) p.set(k, v);
    }
    const s = p.toString();
    return s ? `?${s}` : "";
  })();
  const withTracking = (href) => {
    if (!href || !trackingQs) return href;
    return href.includes("?") ? `${href}&${trackingQs.slice(1)}` : `${href}${trackingQs}`;
  };

  useEffect(() => {
    document.title = "Colucci Assicuratori — Aziende";
    const meta = (name, content, isProperty = false) => {
      const attr = isProperty ? "property" : "name";
      let tag = document.querySelector(`meta[${attr}="${name}"]`);
      if (!tag) { tag = document.createElement("meta"); tag.setAttribute(attr, name); document.head.appendChild(tag); }
      tag.setAttribute("content", content);
    };
    meta("description", "Soluzioni assicurative per imprese, professionisti con P.IVA, PMI e grandi aziende. Polizza CAT NAT obbligatoria 2026, RC Professionale, Cyber, D&O. Consulenza e preventivi gratuiti.");
    meta("keywords", "assicurazioni imprese, assicurazione CAT NAT obbligo 2026, polizza catastrofi naturali imprese, RC Professionale, Cyber Risk impresa, D&O amministratori");
    // JSON-LD structured data
    const ld = document.getElementById("ld-aziende") || (() => { const s = document.createElement("script"); s.id = "ld-aziende"; s.type = "application/ld+json"; document.head.appendChild(s); return s; })();
    ld.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "InsuranceAgency",
      name: "Colucci Assicuratori — Polizze Aziende",
      description: "Assicurazioni per imprese e partite IVA con sede in Italia",
      serviceType: "Business Insurance",
      areaServed: "IT",
      telephone: "+39 081 7611000",
    });
  }, []);

  return (
    <div className="min-h-screen bg-[#F5F4F0]">
      <Header />

      {/* HERO */}
      <section className="bg-gradient-to-br from-[#163321] via-[#1F4530] to-[#163321] text-white py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-white/60 mb-4">
            <Briefcase className="w-4 h-4" />
            <span>Area Aziende</span>
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl leading-tight mb-5">
            Soluzioni assicurative per <span className="text-[#D97B58]">imprese e partite IVA</span>
          </h1>
          <p className="text-lg text-white/80 max-w-2xl leading-relaxed">
            Proteggiamo la tua attività a 360°: dalla polizza CAT NAT obbligatoria ai rischi cyber, dalla responsabilità professionale alla protezione degli amministratori.
          </p>
          <div className="mt-7 flex flex-wrap gap-3">
            <Link to={withTracking("/aziende/catnat/quotatore")} className="inline-flex items-center gap-2 bg-[#D97B58] hover:bg-[#C16A48] transition-colors px-6 py-3 rounded-full font-semibold text-sm">
              <AlertTriangle className="w-4 h-4" /> Quota CAT NAT (obbligo 2026)
            </Link>
            <a href="tel:+39081680568" className="inline-flex items-center gap-2 border border-white/30 hover:bg-white/10 transition-colors px-6 py-3 rounded-full font-semibold text-sm">
              <Phone className="w-4 h-4" /> 081 680 568
            </a>
          </div>
        </div>
      </section>

      {/* ELENCO POLIZZE */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 py-14">
        <h2 className="font-heading text-3xl text-[#163321] mb-2">Elenco polizze Aziende</h2>
        <p className="text-sm text-[#4A5E51] mb-8">Clicca sulla polizza che ti interessa per approfondire o richiedere preventivo.</p>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {POLIZZE_AZIENDE.map((p) => {
            const Icon = p.icon;
            const isObbligo = p.obbligatoria;
            return (
              <div
                key={p.id}
                className={`relative rounded-2xl border-2 p-5 flex flex-col gap-3 transition-all hover:shadow-lg ${
                  isObbligo
                    ? "bg-amber-50 border-amber-500 shadow-md"
                    : p.comingSoon
                    ? "bg-[#F5F4F0]/60 border-[#E5E2DB] hover:border-[#163321]/30"
                    : "bg-white border-[#E5E2DB] hover:border-[#163321]/30"
                }`}
                data-testid={`polizza-card-${p.id}`}
              >
                {p.tagText && (
                  <span className={`absolute -top-2 left-5 text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded ${p.tagColor}`}>{p.tagText}</span>
                )}
                {p.comingSoon && (
                  <span className="absolute -top-2 left-5 text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded bg-[#163321] text-[#D9C49A]" data-testid={`polizza-coming-soon-${p.id}`}>
                    Prossimamente — su richiesta
                  </span>
                )}
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${isObbligo ? "bg-amber-500 text-white" : p.comingSoon ? "bg-[#4A5E51] text-white" : "bg-[#163321] text-white"}`}>
                  <Icon className="w-5 h-5" />
                </div>
                <h3 className="font-heading text-lg text-[#0F1F14] leading-tight">{p.nome}</h3>
                <p className="text-sm text-[#4A5E51] flex-1 leading-relaxed">{p.short}</p>
                {p.href ? (
                  <div className="flex flex-wrap gap-2 pt-2">
                    <Link to={withTracking(p.ctaHref || p.href)}
                          data-testid={`polizza-card-cta-${p.id}`}
                          className={`inline-flex items-center gap-1 text-sm font-semibold rounded-full px-4 py-2 ${isObbligo ? "bg-amber-500 hover:bg-amber-600 text-white" : "bg-[#163321] hover:bg-[#0F2F1F] text-white"}`}>
                      {p.ctaLabel} <ArrowRight className="w-3.5 h-3.5" />
                    </Link>
                    <Link to={withTracking(p.href)}
                          data-testid={`polizza-card-info-${p.id}`}
                          className="inline-flex items-center gap-1 text-sm font-semibold rounded-full px-4 py-2 border border-[#163321]/30 text-[#163321] hover:bg-[#163321]/5">
                      Scopri di più
                    </Link>
                  </div>
                ) : p.comingSoon ? (
                  <div className="flex flex-wrap gap-2 pt-2"
                        data-testid={`polizza-card-coming-soon-${p.id}`}>
                    <Link
                      to={withTracking(`/consulenza?prodotto=${p.id}`)}
                      data-testid={`polizza-card-cta-${p.id}`}
                      className="inline-flex items-center gap-1 text-sm font-semibold rounded-full px-4 py-2 bg-[#163321] hover:bg-[#0F2F1F] text-white transition"
                    >
                      Preventivo su richiesta — Contattaci <ArrowRight className="w-3.5 h-3.5" />
                    </Link>
                  </div>
                ) : (
                  <Link
                    to={withTracking(`/questionario/aziende?polizza=${AZIENDA_ID_TO_BACKEND_SLUG[p.id] || "altro"}`)}
                    state={{ tipologia: p.id, polizza_nome: p.nome }}
                    data-testid={`polizza-card-cta-${p.id}`}
                    className="inline-flex items-center gap-1 text-sm font-semibold text-[#163321] hover:underline pt-2"
                  >
                    {p.ctaLabel} <ArrowRight className="w-3.5 h-3.5" />
                  </Link>
                )}
              </div>
            );
          })}
        </div>
      </section>

      {/* VALUE PROPOSITION */}
      <section className="bg-white border-y border-[#E5E2DB] py-14">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <h2 className="font-heading text-3xl text-[#163321] mb-8">Perché scegliere Colucci Assicuratori</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {VALUE_PROP.map((v, i) => {
              const Icon = v.icon;
              return (
                <div key={i} className="space-y-2">
                  <div className="w-10 h-10 rounded-xl bg-[#E6F4EA] text-[#163321] flex items-center justify-center">
                    <Icon className="w-5 h-5" />
                  </div>
                  <h3 className="font-semibold text-[#0F1F14]">{v.title}</h3>
                  <p className="text-sm text-[#4A5E51]">{v.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 py-14">
        <h2 className="font-heading text-3xl text-[#163321] mb-6">Domande frequenti — Aziende</h2>
        <div className="space-y-3">
          {FAQ_AZIENDE.map((f, i) => (
            <details key={i} className="bg-white border border-[#E5E2DB] rounded-xl p-4 group">
              <summary className="cursor-pointer font-semibold text-[#0F1F14] list-none flex justify-between items-center text-sm">
                {f.q}
                <span className="text-[#163321] ml-2 group-open:rotate-45 transition-transform">+</span>
              </summary>
              <p className="mt-3 text-sm text-[#4A5E51] leading-relaxed">{f.a}</p>
            </details>
          ))}
        </div>
      </section>

      {/* CTA FINALE */}
      <section className="bg-[#163321] text-white py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <h2 className="font-heading text-3xl mb-3">Parla con un consulente</h2>
          <p className="text-white/70 text-sm mb-5 max-w-xl mx-auto">Analizziamo insieme i rischi della tua impresa e costruiamo il pacchetto assicurativo più efficace al miglior prezzo di mercato.</p>
          <a href="tel:+39081680568" className="inline-flex items-center gap-2 bg-[#D97B58] hover:bg-[#C16A48] transition-colors px-7 py-3 rounded-full font-semibold">
            <Phone className="w-4 h-4" /> 081 680 568
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
}
